// ============================================================================
// ADVOCATE FORM MODAL — Add / Edit Advocate (MSTR-ADV-001)
// ============================================================================
// Large 800px. Address block with country→state cascade dropdown from bms.
//
// Default country on Add: India. recsuit is India-first; ~all advocates are
// in India. Change DEFAULT_COUNTRY_CODE if you ever target another region.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { advocateService } from 'api/services/advocateService';
import { countryService } from 'api/services/countryService';
import { stateProvinceService } from 'api/services/stateProvinceService';
import type { Advocate } from 'api/types/advocate.types';

type AdvocateStatus = 'ACTIVE' | 'INACTIVE';

/** Default country on Add (recsuit is India-first). */
const DEFAULT_COUNTRY_CODE = 'IN';

interface AdvocateFormData {
  advocateCode: string;
  advocateName: string;
  barCouncilRegNo: string;
  specialisation: string;
  empanelledFrom: string;
  empanelledTo: string;
  email: string;
  phoneNumber: string;

  addressLine1: string;
  addressLine2: string;
  city: string;
  countryId: string;
  stateProvinceId: string;
  pincode: string;

  statusCode: AdvocateStatus;
}

const INITIAL_FORM_DATA: AdvocateFormData = {
  advocateCode: '',
  advocateName: '',
  barCouncilRegNo: '',
  specialisation: '',
  empanelledFrom: '',
  empanelledTo: '',
  email: '',
  phoneNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  countryId: '',
  stateProvinceId: '',
  pincode: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const dateOnly = (iso?: string | null) => (iso ? iso.split('T')[0] : '');

type SelectOption = { value: string; label: string };

export interface AdvocateFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: Advocate | null;
  onSaved: () => void;
  onClose: () => void;

  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const AdvocateFormModal: React.FC<AdvocateFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<AdvocateFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData); formDataRef.current = formData;
  const recordRef = useRef(record); recordRef.current = record;
  const initialSnapshotRef = useRef('');
  const modeRef = useRef(mode); modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  // --- FK lookup state ---
  const [countryOptions, setCountryOptions] = useState<SelectOption[]>([]);
  const [stateOptions, setStateOptions] = useState<SelectOption[]>([]);
  // Seed option for preserving inactive parents on edit
  const [seedStateOption, setSeedStateOption] = useState<SelectOption | null>(null);
  const [seedCountryOption, setSeedCountryOption] = useState<SelectOption | null>(null);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });
  }, []);

  // API caps pageSize at 100 (QueryParameters.MaxPageSize). Pagination is
  // therefore required for lookups with >100 rows — e.g. Country has 249 ISO
  // entries, and India's sort_order=103 pushes it past the first page.
  const loadCountries = useCallback(async () => {
    try {
      const all: Array<{ id: string; countryCode: string; countryName: string }> = [];
      for (let page = 1; page <= 10; page++) {
        const res = await countryService.getAll({ page, pageSize: 100 });
        all.push(...res.data);
        if (!res.pagination.hasNextPage) break;
      }
      const opts = all.map(c => ({
        value: c.id,
        label: `${c.countryName} (${c.countryCode})`,
      }));
      setCountryOptions(opts);
      return all;
    } catch {
      setCountryOptions([]);
      return [];
    }
  }, []);

  const loadStates = useCallback(async (countryId: string) => {
    if (!countryId) { setStateOptions([]); return; }
    try {
      const all: Array<{ id: string; provinceCode: string; provinceName: string }> = [];
      for (let page = 1; page <= 10; page++) {
        const res = await stateProvinceService.getAll({ page, pageSize: 100, countryId });
        all.push(...res.data);
        if (!res.pagination.hasNextPage) break;
      }
      const opts = all.map(s => ({
        value: s.id,
        label: `${s.provinceName} (${s.provinceCode})`,
      }));
      setStateOptions(opts);
    } catch {
      setStateOptions([]);
    }
  }, []);

  // Populate form on open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }

    const rec = recordRef.current;

    (async () => {
      // Load country list first so create mode can resolve the India default
      // before the initial snapshot is captured (prevents false isDirty).
      const countries = await loadCountries();

      let data: AdvocateFormData;
      if (rec) {
        data = {
          advocateCode: rec.advocateCode,
          advocateName: rec.advocateName,
          barCouncilRegNo: rec.barCouncilRegNo ?? '',
          specialisation: rec.specialisation ?? '',
          empanelledFrom: dateOnly(rec.empanelledFrom),
          empanelledTo: dateOnly(rec.empanelledTo),
          email: rec.email ?? '',
          phoneNumber: rec.phoneNumber ?? '',
          addressLine1: rec.addressLine1 ?? '',
          addressLine2: rec.addressLine2 ?? '',
          city: rec.city ?? '',
          countryId: rec.countryId ?? '',
          stateProvinceId: rec.stateProvinceId ?? '',
          pincode: rec.pincode ?? '',
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as AdvocateStatus,
        };
      } else {
        const defaultCountry = countries.find(c => c.countryCode === DEFAULT_COUNTRY_CODE);
        data = {
          ...INITIAL_FORM_DATA,
          countryId: defaultCountry?.id ?? '',
        };
      }

      setFormData(data);
      initialSnapshotRef.current = JSON.stringify(data);
      setFieldErrors({});

      // Seed country/state labels in case they are now inactive (edit only)
      if (rec) {
        if (rec.countryId && (rec.countryCode || rec.countryName)) {
          setSeedCountryOption({
            value: rec.countryId,
            label: `${rec.countryName} (${rec.countryCode})`,
          });
        } else setSeedCountryOption(null);
        if (rec.stateProvinceId && (rec.stateProvinceCode || rec.stateProvinceName)) {
          setSeedStateOption({
            value: rec.stateProvinceId,
            label: `${rec.stateProvinceName} (${rec.stateProvinceCode})`,
          });
        } else setSeedStateOption(null);
      } else {
        setSeedCountryOption(null);
        setSeedStateOption(null);
      }

      // Load states for whichever country ended up selected (defaulted or from record)
      if (data.countryId) loadStates(data.countryId);
      else setStateOptions([]);
    })();
  }, [isOpen]);

  // When country changes, refetch states and clear state if previously selected state
  // no longer belongs to the selected country
  const onCountryChange = useCallback((val: string) => {
    clearFieldError('countryId');
    setFormData(p => {
      // If country cleared, clear state as well
      if (!val) {
        return { ...p, countryId: '', stateProvinceId: '' };
      }
      // If country changed, clear state (we'll refetch)
      if (p.countryId !== val) {
        return { ...p, countryId: val, stateProvinceId: '' };
      }
      return { ...p, countryId: val };
    });
    if (val) loadStates(val); else setStateOptions([]);
  }, [clearFieldError, loadStates]);

  const combinedCountryOptions = useMemo(() => {
    const blank = [{ value: '', label: '— No country —' }];
    if (!seedCountryOption) return [...blank, ...countryOptions];
    const seedId = seedCountryOption.value;
    return [
      ...blank,
      ...countryOptions.filter(o => o.value !== seedId),
      seedCountryOption,
    ];
  }, [countryOptions, seedCountryOption]);

  const combinedStateOptions = useMemo(() => {
    const blank = [{ value: '', label: '— No state —' }];
    if (!seedStateOption) return [...blank, ...stateOptions];
    const seedId = seedStateOption.value;
    return [
      ...blank,
      ...stateOptions.filter(o => o.value !== seedId),
      seedStateOption,
    ];
  }, [stateOptions, seedStateOption]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.advocateCode || currentFormData.advocateCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.advocateCode?.trim();
      const name = currentFormData.advocateName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.advocateCode || currentRecord?.advocateCode || 'Advocate'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        // Reload states for restored country
        if (savedFormData.countryId) loadStates(savedFormData.countryId);
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.advocateCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, loadStates]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.advocateCode.trim();
    const name = formData.advocateName.trim();
    const email = formData.email.trim();
    const pin = formData.pincode.trim();

    if (!code) errors.advocateCode = 'Advocate code is required';
    else if (code.length > 30) errors.advocateCode = 'Max 30 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.advocateCode = 'Uppercase letters, digits, hyphens only';

    if (!name) errors.advocateName = 'Advocate name is required';
    else if (name.length > 200) errors.advocateName = 'Max 200 characters';

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Invalid email format';

    if (pin && !/^\d{4,10}$/.test(pin)) errors.pincode = 'Pincode must be 4-10 digits';

    // Date range check
    if (formData.empanelledFrom && formData.empanelledTo
        && formData.empanelledFrom > formData.empanelledTo) {
      errors.empanelledTo = 'Empanelled To must be on or after Empanelled From';
    }

    // State requires country
    if (formData.stateProvinceId && !formData.countryId) {
      errors.countryId = 'Country is required when a state is selected';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector<HTMLInputElement>('input')?.focus(); }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.advocateCode.trim().toUpperCase();
      const name = formData.advocateName.trim();

      const payload = {
        advocateCode: code,
        advocateName: name,
        barCouncilRegNo: formData.barCouncilRegNo.trim() || null,
        specialisation: formData.specialisation.trim() || null,
        empanelledFrom: formData.empanelledFrom || null,
        empanelledTo: formData.empanelledTo || null,
        email: formData.email.trim() || null,
        phoneNumber: formData.phoneNumber.trim() || null,
        addressLine1: formData.addressLine1.trim() || null,
        addressLine2: formData.addressLine2.trim() || null,
        city: formData.city.trim() || null,
        stateProvinceId: formData.stateProvinceId || null,
        countryId: formData.countryId || null,
        pincode: formData.pincode.trim() || null,
        statusCode: formData.statusCode,
      };

      if (mode === 'create' || mode === 'copy') {
        await advocateService.create(payload);
        notificationService.toast.success(`Advocate "${code}" created successfully.`);
      } else if (record) {
        await advocateService.update(record.id, { ...payload, rowVersion: record.rowVersion });
        notificationService.toast.success(`Advocate "${code}" updated successfully.`);
      }

      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This advocate was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'An advocate with this code or bar council number already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        notificationService.toast.error((err.response.data.errors as string[]).join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save advocate. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => { setDiscardConfirmOpen(false); onClose(); }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem',
              border: '3px solid var(--border-color)', borderTopColor: '#2563eb',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'view' ? 'View Advocate' : mode === 'copy' ? 'Copy Advocate' : mode === 'create' ? 'Add Advocate' : 'Edit Advocate'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-grid-2">
            <div className="form-group" data-field="advocateCode">
              <label className="form-label">Advocate Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('advocateCode') ? ' form-field-error' : ''}`}
                value={formData.advocateCode}
                maxLength={30}
                placeholder="ADV-001"
                onChange={(e) => { clearFieldError('advocateCode'); setFormData(p => ({ ...p, advocateCode: e.target.value.toUpperCase() })); }}
                disabled={isSaving}
              />
              {getFieldError('advocateCode') && <div className="form-error-message">{getFieldError('advocateCode')}</div>}
            </div>

            <div className="form-group" data-field="advocateName">
              <label className="form-label">Advocate Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('advocateName') ? ' form-field-error' : ''}`}
                value={formData.advocateName}
                maxLength={200}
                placeholder="Adv. Priya Sharma"
                onChange={(e) => { clearFieldError('advocateName'); setFormData(p => ({ ...p, advocateName: e.target.value })); }}
                disabled={isSaving}
              />
              {getFieldError('advocateName') && <div className="form-error-message">{getFieldError('advocateName')}</div>}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="barCouncilRegNo">
              <label className="form-label">Bar Council Reg #</label>
              <input
                type="text"
                className="form-input"
                value={formData.barCouncilRegNo}
                maxLength={50}
                placeholder="D/1234/2015"
                onChange={(e) => setFormData(p => ({ ...p, barCouncilRegNo: e.target.value }))}
                disabled={isSaving}
              />
            </div>

            <div className="form-group" data-field="specialisation">
              <label className="form-label">Specialisation</label>
              <input
                type="text"
                className="form-input"
                value={formData.specialisation}
                maxLength={100}
                placeholder="Banking / NI Act"
                onChange={(e) => setFormData(p => ({ ...p, specialisation: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="empanelledFrom">
              <label className="form-label">Empanelled From</label>
              <input
                type="date"
                className="form-input"
                value={formData.empanelledFrom}
                onChange={(e) => setFormData(p => ({ ...p, empanelledFrom: e.target.value }))}
                disabled={isSaving}
              />
            </div>

            <div className="form-group" data-field="empanelledTo">
              <label className="form-label">Empanelled To</label>
              <input
                type="date"
                className={`form-input${getFieldError('empanelledTo') ? ' form-field-error' : ''}`}
                value={formData.empanelledTo}
                onChange={(e) => { clearFieldError('empanelledTo'); setFormData(p => ({ ...p, empanelledTo: e.target.value })); }}
                disabled={isSaving}
              />
              {getFieldError('empanelledTo') && <div className="form-error-message">{getFieldError('empanelledTo')}</div>}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="email">
              <label className="form-label">Email</label>
              <input
                type="email"
                className={`form-input${getFieldError('email') ? ' form-field-error' : ''}`}
                value={formData.email}
                maxLength={200}
                placeholder="advocate@example.com"
                onChange={(e) => { clearFieldError('email'); setFormData(p => ({ ...p, email: e.target.value })); }}
                disabled={isSaving}
              />
              {getFieldError('email') && <div className="form-error-message">{getFieldError('email')}</div>}
            </div>

            <div className="form-group" data-field="phoneNumber">
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                className="form-input"
                value={formData.phoneNumber}
                maxLength={20}
                placeholder="+91 98XXXXXXXX"
                onChange={(e) => setFormData(p => ({ ...p, phoneNumber: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          </div>

          {/* Address subsection */}
          <div
            style={{
              marginTop: '1rem',
              paddingTop: '0.75rem',
              borderTop: '1px solid var(--border-color)',
            }}
          >
            <div
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: '0.75rem',
              }}
            >
              Address
            </div>

            <div className="form-group" data-field="addressLine1">
              <label className="form-label">Address Line 1</label>
              <input
                type="text"
                className="form-input"
                value={formData.addressLine1}
                maxLength={200}
                placeholder="Chamber No. 12, Bar Association Bldg"
                onChange={(e) => setFormData(p => ({ ...p, addressLine1: e.target.value }))}
                disabled={isSaving}
              />
            </div>

            <div className="form-group" data-field="addressLine2">
              <label className="form-label">Address Line 2</label>
              <input
                type="text"
                className="form-input"
                value={formData.addressLine2}
                maxLength={200}
                placeholder="Near XYZ..."
                onChange={(e) => setFormData(p => ({ ...p, addressLine2: e.target.value }))}
                disabled={isSaving}
              />
            </div>

            <div className="form-grid-2">
              <div className="form-group" data-field="city">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.city}
                  maxLength={100}
                  placeholder="Mumbai"
                  onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))}
                  disabled={isSaving}
                />
              </div>

              <div className="form-group" data-field="pincode">
                <label className="form-label">Pincode</label>
                <input
                  type="text"
                  className={`form-input${getFieldError('pincode') ? ' form-field-error' : ''}`}
                  value={formData.pincode}
                  maxLength={10}
                  placeholder="400001"
                  onChange={(e) => { clearFieldError('pincode'); setFormData(p => ({ ...p, pincode: e.target.value })); }}
                  disabled={isSaving}
                />
                {getFieldError('pincode') && <div className="form-error-message">{getFieldError('pincode')}</div>}
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-group" data-field="countryId">
                <SyutSelect
                  label="Country"
                  options={combinedCountryOptions}
                  value={formData.countryId}
                  onChange={(v) => onCountryChange((v as string) || '')}
                  error={getFieldError('countryId')}
                  isClearable={false}
                  placeholder="— No country —"
                  isDisabled={mode === 'view' || (isSaving)}
                />
              </div>

              <div className="form-group" data-field="stateProvinceId">
                <SyutSelect
                  label="State / Province"
                  options={combinedStateOptions}
                  value={formData.stateProvinceId}
                  onChange={(v) => { clearFieldError('stateProvinceId'); setFormData(p => ({ ...p, stateProvinceId: (v as string) || '' })); }}
                  error={getFieldError('stateProvinceId')}
                  isClearable={false}
                  placeholder={formData.countryId ? '— No state —' : 'Select country first'}
                  isDisabled={mode === 'view' || (isSaving || !formData.countryId)}
                />
              </div>
            </div>
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(v) => setFormData(p => ({ ...p, statusCode: ((v as string) || 'ACTIVE') as AdvocateStatus }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={(mode === 'create' || mode === 'copy')
          ? `Create advocate "${formData.advocateCode.trim().toUpperCase()}"?`
          : `Update advocate "${formData.advocateCode.trim().toUpperCase()}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default AdvocateFormModal;
